import React, { useEffect } from 'react';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://yourfutureprep.com';
const DEFAULT_IMAGE = `${SITE_URL}/yfp_logo.png`;

export type StructuredData = Record<string, unknown>;

export type SEOProps = {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  noIndex?: boolean;
  structuredData?: StructuredData;
};

const setMetaTag = (
  name: string,
  content: string,
  attribute: 'name' | 'property' = 'name'
) => {
  let el = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLinkTag = (rel: string, href: string) => {
  let el = document.querySelector(`link[rel="${rel}"][href]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path = '',
  image = DEFAULT_IMAGE,
  noIndex = false,
  structuredData,
}) => {
  const fullUrl = path ? `${SITE_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}` : SITE_URL;
  const fullImage = image.startsWith('http') ? image : `${SITE_URL.replace(/\/$/, '')}${image.startsWith('/') ? '' : '/'}${image}`;

  useEffect(() => {
    if (title) document.title = title;
    if (description) setMetaTag('description', description);
    if (noIndex) setMetaTag('robots', 'noindex, nofollow');
    setLinkTag('canonical', fullUrl);
    if (title) setMetaTag('og:title', title, 'property');
    if (description) setMetaTag('og:description', description, 'property');
    setMetaTag('og:url', fullUrl, 'property');
    setMetaTag('og:image', fullImage, 'property');
    if (title) setMetaTag('twitter:title', title);
    if (description) setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', fullImage);
    setMetaTag('og:image:width', '319', 'property');
    setMetaTag('og:image:height', '322', 'property');
    setMetaTag('og:image:type', 'image/png', 'property');

    // Structured data (JSON-LD)
    const existingScript = document.querySelector('script[data-seo-jsonld]');
    if (structuredData) {
      const script = existingScript || document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(structuredData);
      if (!existingScript) document.head.appendChild(script);
    } else if (existingScript) {
      existingScript.remove();
    }

    return () => {
      const scriptToRemove = document.querySelector('script[data-seo-jsonld]');
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [title, description, noIndex, fullUrl, fullImage, structuredData]);

  return null;
};

export default SEO;
